import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
//import { useAuth } from "./contexts/CombinedAuthContext";
import { usePlans } from "../contexts/PlansContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBed,
  faHome,
  faPlus,
  faUser,
  faCheck,
  faTimes,
  faMapMarkerAlt,
  faCalendarAlt,
  faDollarSign,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";

// UI Components
import { SkeletonVariants } from "../components/ui/Skeleton";
import { CinematicHero, GlassSection, R5Tabs, R5Card } from "../components/ui";
import { SmartImage } from "../components/OptimizedImage";
import SubscriptionCTADialog from "../components/SubscriptionCTADialog";

import "../styles/RoommateFinder.css";

// Dummy rental listings data
const RENTAL_LISTINGS = [
  {
    id: "rental-1",
    title: "Room in Shared Beachfront Villa",
    type: "Private Room",
    location: "Magens Bay, St. Thomas",
    price: 850,
    available: "2025-05-01",
    term: "Long Term",
    description:
      "Spacious private room in a shared 3-bedroom villa with direct beach access. Includes utilities and wifi.",
    amenities: [
      "Private Bathroom",
      "Shared Kitchen",
      "Beachfront",
      "Wifi",
      "AC",
    ],
    contactName: "Sarah",
    contactEmail: "sarah@example.com",
    images: ["https://via.placeholder.com/300x200?text=Beach+Villa"],
    postedDate: "2025-03-15",
    postedBy: "user-123",
  },
  {
    id: "rental-2",
    title: "Studio Apartment in Cruz Bay",
    type: "Entire Place",
    location: "Cruz Bay, St. John",
    price: 1200,
    available: "2025-04-15",
    term: "Long Term",
    description:
      "Newly renovated studio apartment in the heart of Cruz Bay. Walking distance to restaurants, shops, and ferry.",
    amenities: [
      "Full Kitchen",
      "Wifi",
      "AC",
      "Laundry",
      "Walking Distance to Town",
    ],
    contactName: "Michael",
    contactEmail: "michael@example.com",
    images: ["https://via.placeholder.com/300x200?text=Studio+Apt"],
    postedDate: "2025-03-10",
    postedBy: "user-456",
  },
  {
    id: "rental-3",
    title: "Shared Room for Season Worker",
    type: "Shared Room",
    location: "Red Hook, St. Thomas",
    price: 600,
    available: "2025-04-01",
    term: "Seasonal",
    description:
      "Affordable shared room ideal for seasonal workers. Close to restaurants and marina.",
    amenities: ["Shared Bathroom", "Shared Kitchen", "Wifi", "Parking"],
    contactName: "Jason",
    contactEmail: "jason@example.com",
    images: ["https://via.placeholder.com/300x200?text=Shared+Room"],
    postedDate: "2025-03-20",
    postedBy: "current-user-id", // Assuming this is the current user's listing
  },
];

function RoommateFinder() {
  const navigate = useNavigate();
  //const { isAuthenticated, user } = useAuth();
  //const { memberOrders } = usePlans();
  const plans = usePlans();
  const { isTraveler, isTravelerPlus, isBusinessOwner, loading: plansLoading } = plans || {};
  const [activeTab, setActiveTab] = useState("listings");
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [backgroundCheckStatus] = useState("pending"); // 'pending', 'approved', 'rejected'
  const [filters, setFilters] = useState({
    type: "all",
    priceMin: "",
    priceMax: "",
    location: "all",
  });

  // Check if current user is eligible for roommate finder
  const hasTraveler = typeof isTraveler === "function" ? isTraveler() : false;
  const hasTravelerPlus = typeof isTravelerPlus === "function" ? isTravelerPlus() : false;
  const hasBusinessOwner = typeof isBusinessOwner === "function" ? isBusinessOwner() : false;
  const isEligible = hasTraveler || hasTravelerPlus || hasBusinessOwner;

  // Check if eligible for listing properties (business subscription or property manager)
  const canListProperty = hasBusinessOwner;

  // Simulate fetching data
  useEffect(() => {
    // Simulate network delay
    setTimeout(() => {
      setListings(RENTAL_LISTINGS);
      setLoading(false);
    }, 1000);
  }, []);

  // Handle listing selection
  const handleSelectListing = (listing) => {
    setSelectedListing(listing);
  };

  // Reset listing selection
  const handleBackToList = () => {
    setSelectedListing(null);
  };

  // Filter listings
  const getFilteredListings = () => {
    return listings.filter((listing) => {
      // Filter by type
      if (filters.type !== "all" && listing.type !== filters.type) {
        return false;
      }

      // Filter by price min
      if (filters.priceMin && listing.price < parseInt(filters.priceMin)) {
        return false;
      }

      // Filter by price max
      if (filters.priceMax && listing.price > parseInt(filters.priceMax)) {
        return false;
      }

      // Filter by location
      if (
        filters.location !== "all" &&
        !listing.location.includes(filters.location)
      ) {
        return false;
      }

      return true;
    });
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  // Check if current user posted the listing
  const isMyListing = (listing) => {
    // In a real app, compare with actual user ID
    return listing.postedBy === "current-user-id";
  };

  // Render eligibility check
  const renderEligibilityCheck = () => {
    if (plansLoading) {
      return <SkeletonVariants.Card />;
    }

    if (isEligible) {
      if (backgroundCheckStatus === "pending") {
        return (
          <div className="background-check-required">
            <div className="eligibility-icon">
              <FontAwesomeIcon icon={faShieldAlt} size="3x" />
            </div>
            <h2>Background Check Required</h2>
            <p>
              For everyone's safety, we require a background check before
              accessing roommate listings. This is a one-time process.
            </p>
            <div className="background-check-buttons">
              <button className="primary-btn">Start Background Check</button>
              <button className="secondary-btn">Learn More</button>
            </div>
          </div>
        );
      } else if (backgroundCheckStatus === "rejected") {
        return (
          <div className="background-check-rejected">
            <div className="eligibility-icon">
              <FontAwesomeIcon icon={faTimes} size="3x" />
            </div>
            <h2>Background Check Not Approved</h2>
            <p>
              Unfortunately, we were unable to approve your background check. If
              you believe this is an error, please contact support.
            </p>
            <button className="primary-btn">Contact Support</button>
          </div>
        );
      }
    } else {
      return (
        <SubscriptionCTADialog
          feature="Roommate Finder"
          description="Connect with vetted travellers and find shared housing across the USVI. Browse member-posted rooms, private apartments, and long-stay rentals — all verified by the Guild."
          benefits={[
            'Browse rooms and apartments across St. Thomas, St. John & St. Croix',
            'Filter by price, term, and location',
            'Contact hosts directly through the Guild',
            'List your own property for free',
            'Background-check verified members only',
          ]}
        />
      );
    }

    return null;
  };

  // Render rental listing detail
  const renderListingDetail = () => {
    if (!selectedListing) return null;

    return (
      <div className="listing-detail">
        <button className="back-button" onClick={handleBackToList}>
          ← Back to Listings
        </button>

        <div className="listing-header">
          <h2>{selectedListing.title}</h2>
          <div className="listing-price">${selectedListing.price}/month</div>
        </div>

        <div className="listing-images">
          {selectedListing.images.map((image, index) => (
            <SmartImage
              key={index}
              src={image}
              alt={`${selectedListing.title} - ${index + 1}`}
              className="listing-images__item"
              aspectRatio={4 / 3}
              objectFit="cover"
              priority={index === 0}
            />
          ))}
        </div>

        <div className="listing-content">
          <div className="listing-main-info">
            <div className="listing-detail-item">
              <FontAwesomeIcon icon={faHome} />
              <span>{selectedListing.type}</span>
            </div>

            <div className="listing-detail-item">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>{selectedListing.location}</span>
            </div>

            <div className="listing-detail-item">
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>
                Available:{" "}
                {new Date(selectedListing.available).toLocaleDateString()}
              </span>
            </div>

            <div className="listing-detail-item">
              <FontAwesomeIcon icon={faDollarSign} />
              <span>${selectedListing.price}/month</span>
            </div>
          </div>

          <div className="listing-description">
            <h3>Description</h3>
            <p>{selectedListing.description}</p>
          </div>

          <div className="listing-amenities">
            <h3>Amenities</h3>
            <ul>
              {selectedListing.amenities.map((amenity, index) => (
                <li key={index}>
                  <FontAwesomeIcon icon={faCheck} />
                  <span>{amenity}</span>
                </li>
              ))}
            </ul>
          </div>

          {!isMyListing(selectedListing) && (
            <div className="listing-contact">
              <h3>Contact Information</h3>
              <p>
                <strong>Contact:</strong> {selectedListing.contactName}
              </p>
              <p>
                <strong>Email:</strong> {selectedListing.contactEmail}
              </p>
              <button className="contact-btn">
                <FontAwesomeIcon icon={faUser} /> Contact Lister
              </button>
            </div>
          )}

          {isMyListing(selectedListing) && (
            <div className="my-listing-controls">
              <h3>Listing Controls</h3>
              <div className="listing-buttons">
                <button className="edit-listing-btn">Edit Listing</button>
                <button className="delete-listing-btn">Delete Listing</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render filter panel
  // Render filter sidebar — R5-M1 sticky aside with brass form fields.
  const renderFilterPanel = () => {
    return (
      <aside className="r5-roommate-filters">
        <h4>Filter Berths</h4>
        <div className="r5-field">
          <label htmlFor="rmf-type">Type</label>
          <select id="rmf-type" name="type" value={filters.type} onChange={handleFilterChange}>
            <option value="all">All Types</option>
            <option value="Private Room">Private Room</option>
            <option value="Shared Room">Shared Room</option>
            <option value="Entire Place">Entire Place</option>
          </select>
        </div>
        <div className="r5-field">
          <label htmlFor="rmf-loc">Island</label>
          <select id="rmf-loc" name="location" value={filters.location} onChange={handleFilterChange}>
            <option value="all">All Islands</option>
            <option value="St. Thomas">St. Thomas</option>
            <option value="St. John">St. John</option>
            <option value="St. Croix">St. Croix</option>
          </select>
        </div>
        <div className="r5-field">
          <label htmlFor="rmf-min">Min $</label>
          <input id="rmf-min" type="number" name="priceMin" placeholder="800" value={filters.priceMin} onChange={handleFilterChange} />
        </div>
        <div className="r5-field">
          <label htmlFor="rmf-max">Max $</label>
          <input id="rmf-max" type="number" name="priceMax" placeholder="2400" value={filters.priceMax} onChange={handleFilterChange} />
        </div>
        <button
          type="button"
          className="r5-pack-cta r5-pack-cta--bright"
          style={{ width: "100%", marginTop: 8 }}
          onClick={() =>
            setFilters({ type: "all", priceMin: "", priceMax: "", location: "all" })
          }
        >
          Reset Filters
        </button>
      </aside>
    );
  };

  // Render rental listings — R5-M1 listing grid with R5Card price overlay.
  const renderRentalListings = () => {
    const filteredListings = getFilteredListings();

    if (filteredListings.length === 0) {
      return (
        <div className="no-listings">
          <p>No listings match your filters. Try changing your search criteria.</p>
        </div>
      );
    }

    return (
      <div className="r5-grid--listing">
        {filteredListings.map((listing) => (
          <R5Card
            key={listing.id}
            onClick={() => handleSelectListing(listing)}
            badge={isMyListing(listing) ? "Your Listing" : undefined}
            imageSrc={listing.images[0]}
            imageAlt={listing.title || ''}
            priceOverlay={`$${listing.price} / mo`}
            category={listing.type}
            title={listing.title}
            blurb={listing.location}
            meta={
              <>
                <span>
                  <FontAwesomeIcon icon={faCalendarAlt} />{" "}
                  {new Date(listing.available).toLocaleDateString()}
                </span>
                <strong>View Berth →</strong>
              </>
            }
          />
        ))}
      </div>
    );
  };

  // Render the property listing form
  const renderListPropertyForm = () => {
    if (plansLoading) {
      return <SkeletonVariants.Card />;
    }

    if (!canListProperty) {
      return (
        <div className="business-upgrade-needed">
          <h2>List Your Property</h2>
          <p>You need a business subscription to list properties for rent.</p>
          <button className="upgrade-btn" type="button" onClick={() => navigate("/account/subscriptions")}>Upgrade to Business</button>
        </div>
      );
    }

    // In a real app, this would be a form to add a new property listing
    return (
      <div className="list-property-form">
        <h2>List Your Property</h2>
        <p className="form-description">
          Create a listing for your rental property to find quality tenants.
        </p>

        <form className="property-form">
          <div className="form-group">
            <label>Listing Title</label>
            <input type="text" placeholder="e.g. Cozy Studio in Cruz Bay" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Property Type</label>
              <select>
                <option value="">Select Type</option>
                <option>Private Room</option>
                <option>Shared Room</option>
                <option>Entire Place</option>
              </select>
            </div>

            <div className="form-group">
              <label>Monthly Rent (USD)</label>
              <input type="number" placeholder="e.g. 1200" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Location</label>
              <select>
                <option value="">Select Location</option>
                <option>Charlotte Amalie, St. Thomas</option>
                <option>Red Hook, St. Thomas</option>
                <option>Cruz Bay, St. John</option>
                <option>Coral Bay, St. John</option>
                <option>Christiansted, St. Croix</option>
                <option>Frederiksted, St. Croix</option>
              </select>
            </div>

            <div className="form-group">
              <label>Available From</label>
              <input type="date" />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              rows="4"
              placeholder="Describe your property..."
            ></textarea>
          </div>

          <div className="form-group">
            <label>Amenities</label>
            <div className="amenities-checkboxes">
              <div className="amenity-checkbox">
                <input type="checkbox" id="wifi" />
                <label htmlFor="wifi">WiFi</label>
              </div>
              <div className="amenity-checkbox">
                <input type="checkbox" id="ac" />
                <label htmlFor="ac">Air Conditioning</label>
              </div>
              <div className="amenity-checkbox">
                <input type="checkbox" id="kitchen" />
                <label htmlFor="kitchen">Kitchen</label>
              </div>
              <div className="amenity-checkbox">
                <input type="checkbox" id="parking" />
                <label htmlFor="parking">Parking</label>
              </div>
              <div className="amenity-checkbox">
                <input type="checkbox" id="laundry" />
                <label htmlFor="laundry">Laundry</label>
              </div>
              <div className="amenity-checkbox">
                <input type="checkbox" id="private-bath" />
                <label htmlFor="private-bath">Private Bathroom</label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Upload Photos</label>
            <div className="photo-upload-area">
              <div className="upload-placeholder">
                <FontAwesomeIcon icon={faPlus} />
                <span>Click to Upload</span>
              </div>
            </div>
          </div>

          <div className="form-buttons">
            <button type="submit" className="submit-listing-btn">
              Create Listing
            </button>
            <button type="button" className="cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Main render
  const isApprovedAndEligible = isEligible && backgroundCheckStatus === "approved";
  const roommateTabs = isApprovedAndEligible
    ? [
        { key: "listings", label: "Rental Listings", icon: <FontAwesomeIcon icon={faBed} /> },
        ...(canListProperty
          ? [{ key: "list-property", label: "List Your Property", icon: <FontAwesomeIcon icon={faPlus} /> }]
          : []),
      ]
    : [];

  return (
    <>
      <CinematicHero
        eyebrow="· Roommates & Rentals ·"
        title="Find Your Crew"
        dropCap="F"
        lede="Browse member-vetted housing across the USVI and find a crew that suits your watch."
      />
      {roommateTabs.length > 0 && (
        <R5Tabs
          tabs={roommateTabs}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      )}
      <GlassSection className="roommate-finder-page">
      <div className="page-content">
        {loading ? (
          <div className="listings-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonVariants.Card key={i} />
            ))}
          </div>
        ) : (
          <>
            {/* Eligibility check */}
            {!isApprovedAndEligible ? (
              renderEligibilityCheck()
            ) : (
              <>
                {activeTab === "listings" && (
                  <>
                    {selectedListing ? (
                      renderListingDetail()
                    ) : (
                      <div className="r5-grid--with-aside">
                        {renderFilterPanel()}
                        <div>{renderRentalListings()}</div>
                      </div>
                    )}
                  </>
                )}

                {activeTab === "list-property" && renderListPropertyForm()}
              </>
            )}
          </>
        )}
      </div>
      </GlassSection>
    </>
  );
}

export default RoommateFinder;
